import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, of, catchError, switchMap, takeUntil } from 'rxjs';
import { AddButtonComponent } from '@shared/components/add-button/add-button.component';
import { CashReconciliationService } from './cash-reconciliation.service';
import { EmployeeService } from '../../services/employee.service';
import { ProfileService } from '@services/profile.service';
import { Shift } from '../../models/employee.models';
import {
  CustomDropdownComponent,
  CustomDropdownOption,
} from './components/custom-dropdown/custom-dropdown.component';
import { CurrencyActionInputComponent } from '@shared/components/currency-action-input/currency-action-input.component';
import { StatusBadgeComponent } from './components/status-badge/status-badge.component';
import { EmptyResultComponent } from '@shared/components/empty-result/empty-result.component';
import {
  PaginationBarComponent,
  PaginationData,
} from '@shared/components/pagination-bar/pagination-bar.component';

import { ReconciliationGridComponent } from './components/reconciliation-grid/reconciliation-grid.component';
import {
  ReconciliationGridEmployee,
  ReconciliationGridSavePayload,
  CashReconciliationEntry,
  CashReconciliationReportResponse,
  CashReconciliationReportRow,
  CashReconciliationUpdatePayload,
  CashTransaction,
  FixReconciliationEmployee,
  RowDraft,
} from './interfaces/cash-reconciliation.interface';
import {
  MultiSelectComponent,
  MultiSelectOption,
} from '@shared/components/wi-multi-select/wi-multi-select.component';
import {
  DateRangePickerComponent,
  DateRangeChange,
} from '@shared/components/wi-date-range-picker/wi-date-range-picker.component';
import { DataModalComponent } from '@shared/components/wi-data-modal/wi-data-modal.component';

import {
  DEFAULT_STATUS_TRIGGER,
  DELTA_PILL,
  ShiftOption,
  STATUS_OPTION_LIST,
  STATUS_TRIGGER,
} from './constants/cash-reconciliation.constants';

import {
  mapTransactionsResponse,
  mapFixDataResponse,
  mapReportToGrid,
  formatCurrency,
} from './utils/cash-reconciliation.mapper';

import {
  normalizeStatus,
  getShiftLabel,
  getStatusLabel,
  getStatusStyle,
  normalizeDeltaStatus,
  shouldShowDelta,
  getOpeningStatusLabel,
  getOpeningButtonLabel,
  getDeltaMatchStatus,
  getDeltaStatusLabel,
  getDeltaStatusStyle,
  isPlainDeltaStatus,
} from './helpers/reconciliation.helper';
import { disableFutureDates, getCashReconciliationToday } from './helpers/date.helper';
import { DropdownToggleHelper } from '@shared/helpers/dropdown-toggle.helper';

import { statusIsPlain, getStatusConfig } from './utils/cash-reconciliation.utils';
import { TableUtils, DragScrollConfig } from './utils/table.utils';

@Component({
  selector: 'app-cash-reconciliation',
  standalone: true,
  templateUrl: './cash-reconciliation.component.html',
  styleUrls: ['../../employee.component.scss', './cash-reconciliation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    AddButtonComponent,
    CustomDropdownComponent,
    CurrencyActionInputComponent,
    StatusBadgeComponent,
    EmptyResultComponent,
    PaginationBarComponent,
    MultiSelectComponent,
    ReconciliationGridComponent,
    DateRangePickerComponent,
    DataModalComponent,
    MatTooltipModule,
  ],
})
export class CashReconciliationComponent implements OnInit, OnDestroy, AfterViewInit {
  isTouch =
    typeof window !== 'undefined'
      ? 'ontouchstart' in window || navigator.maxTouchPoints > 0
      : false;

  private readonly service = inject(CashReconciliationService);
  private readonly profileService = inject(ProfileService);
  private readonly employeeService = inject(EmployeeService);
  private readonly messageService = inject(NotificationService);
  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private readonly destroy$ = new Subject<void>();
  private readonly loadEntries$ = new Subject<void>();
  private readonly cdr = inject(ChangeDetectorRef);

  /** Selected calendar day for GET /cash-reconciliation?date=YYYY-MM-DD */
  reconciliationViewDate: Date = getCashReconciliationToday();
  rows: CashReconciliationEntry[] = [];
  loading = false;
  /** Total rows from the last list response (for pagination visibility). */
  totalItems = 0;
  currentPage = 1;
  itemsPerPage = 10;
  private draftById: Record<string, RowDraft> = {};
  readonly UNASSIGNED = 'UNASSIGNED';
  private shiftIdToOptionMap = new Map<string, ShiftOption>();

  locationSelectOptions: CustomDropdownOption[] = [];

  /**
   * Only one of Status/Shift open at a time. Location uses internal dropdown state.
   */
  activeDropdown: { rowId: string; type: 'status' | 'shift' | 'location' } | null = null;

  /** Add-cash popover: which row, amount draft */
  addCashFor: CashReconciliationEntry | null = null;
  addCashDraft = 0;

  /** Payment details modal */
  paymentModal: CashReconciliationEntry | null = null;
  paymentTx: CashTransaction[] = [];
  paymentTxLoading = false;
  paymentModalOpen = false;

  // Fix-past-data modal state
  fixPastModalOpen = false;
  fixStartDate: Date | null = null;
  fixEndDate: Date | null = null;
  fixSearchLoading = false;
  fixHasSearched = false;
  fullData: ReconciliationGridEmployee[] = [];
  displayedData: ReconciliationGridEmployee[] = [];
  /** Snapshot after last successful load/save for per-employee cancel + change map. */
  fixResultsSnapshot: ReconciliationGridEmployee[] = [];
  /**
   * Dirty entry values vs fixResultsSnapshot (per spec).
   * [employeeId][entryId] → current edited balances.
   */
  changedEntriesMap: Record<
    string,
    Record<string, { opening_balance: number | null; closing_balance: number | null }>
  > = {};
  fixShowResultsView = false;
  savingEmployeeIds: string[] = [];

  savingOpeningIds: string[] = [];
  savingClosingIds: string[] = [];
  savingExtraCashIds: string[] = [];

  employeeOptions: MultiSelectOption[] = [];
  selectedEmployeeIds: string[] = [];

  readonly getShiftLabel = getShiftLabel;
  readonly getStatusLabel = getStatusLabel;
  readonly getStatusStyle = getStatusStyle;
  readonly shouldShowDelta = shouldShowDelta;

  readonly getOpeningStatusLabel = getOpeningStatusLabel;
  readonly getOpeningButtonLabel = getOpeningButtonLabel;

  readonly getDeltaMatchStatus = getDeltaMatchStatus;
  readonly getDeltaStatusLabel = getDeltaStatusLabel;
  readonly getDeltaStatusStyle = getDeltaStatusStyle;
  readonly isPlainDeltaStatus = isPlainDeltaStatus;

  readonly formatCurrency = formatCurrency;
  readonly statusIsPlain = statusIsPlain;
  readonly getStatusConfig = getStatusConfig;

  readonly disableFutureDates = disableFutureDates;

  readonly statusDropdownOptions: CustomDropdownOption[] = STATUS_OPTION_LIST.map(o => ({
    value: o.value,
    label: o.label,
  }));

  shiftDropdownOptions: CustomDropdownOption[] = [];
  readonly DELTA_PILL = DELTA_PILL;
  paymentTooltip = 'View cash transactions';
  reportGridData: ReconciliationGridEmployee[] = [];
  selectedReportEmployeeId: string | null = null;

  // Generate Reports (step 1: type, step 2: result)
  reportTypeModalOpen = false;
  reportResultModalOpen = false;
  reportDateRangeModalOpen = false;
  selectedReportType: 'daily' | 'monthly' | 'custom' = 'daily';

  /** Generate Report modal — daily picker */
  reportDate: Date = getCashReconciliationToday();
  selectedMonth = getCashReconciliationToday().getMonth() + 1;
  selectedYear = getCashReconciliationToday().getFullYear();
  /** Custom range (inline calendar) */
  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;
  //customRangeMonthCursor = new Date();
  //customRangeCalendarDays: Date[] = [];
  reportLoading = false;
  reportData: CashReconciliationReportRow[] = [];
  reportDateRangeLabel = '';
  reportLocationLabel = '';
  reportGeneratedAt: Date | null = null;
  reportTotalEntries = 0;
  reportTotalMatched = 0;
  reportTotalNotMatched = 0;
  reportSumDeltas = 0;
  calendarPopoverOpen = false;

  @ViewChild('cashTableWrapper')
  cashTableWrapper!: ElementRef;
  private cashDragScrollConfig!: DragScrollConfig;

  ngOnInit(): void {
    this.loadEntries$
      .pipe(
        switchMap(() => {
          this.loading = true;
          this.cdr.markForCheck();
          const date = this.formatDate(this.reconciliationViewDate);
          return this.service
            .getList(date, this.currentPage, this.itemsPerPage)
            .pipe(
              catchError(() => of({ entries: [] as CashReconciliationEntry[], totalItems: 0 }))
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(({ entries, totalItems }) => {
        this.activeDropdown = null;
        this.rows = entries.map(r => ({
          ...r,
          status: normalizeStatus(r.status),
          match_status: normalizeDeltaStatus(r.match_status),
          mismatch_reason: r.mismatch_reason,
          delta: Number(r.delta ?? 0),
          opening_balance: Number(r.opening_balance ?? 0),
          opening_status: r.opening_status ?? 'send',
          closing_balance:
            r.closing_balance === null || r.closing_balance === undefined
              ? null
              : Number(r.closing_balance),
        }));

        this.totalItems = totalItems;
        this.draftById = {};
        entries.forEach(r => {
          r.shift_id = r.shift_id ?? null;
          this.draftById[r.id] = {
            opening: r.opening_balance,
            opening_status: r.opening_status ?? 'send',
            closing: r.closing_balance,
            delta: Number(r.delta ?? 0),
            match_status: normalizeDeltaStatus(r.match_status),
          };
        });
        this.loading = false;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        requestAnimationFrame(() => this.reinitDragScroll());
      });

    this.loadLocations();
    this.loadShifts();
    this.loadEmployeeOptions();
    this.loadEntries();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    TableUtils.cleanup();
  }

  ngAfterViewInit(): void {
    this.bindAddCashScroll();
  }

  @HostListener('document:click')
  onOutsideClick(): void {
    DropdownToggleHelper.closeAll();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: Event): void {
    const t = ev.target;

    if (t instanceof Element) {
      if (this.activeDropdown) {
        const host = this.hostRef.nativeElement;
        const inDropdown = t.closest('app-custom-dropdown');

        if (!inDropdown || !host.contains(inDropdown)) {
          this.activeDropdown = null;
        }
      }

      // Calendar popover outside click close
      if (this.calendarPopoverOpen) {
        if (t.closest('.cr-calendar-wrap')) {
          return;
        }

        this.calendarPopoverOpen = false;
      }
    }

    if (this.addCashFor) {
      if (t instanceof Element && t.closest('.cr-add-cash')) {
        return;
      }

      this.closeAddCash();
    }
  }

  isDropdownOpen(r: CashReconciliationEntry, type: 'status' | 'shift' | 'location'): boolean {
    if (r.is_grayed) {
      return false;
    }

    return (
      this.activeDropdown !== null &&
      this.activeDropdown.rowId === r.id &&
      this.activeDropdown.type === type
    );
  }

  onDropdownOpenChange(
    open: boolean,
    r: CashReconciliationEntry,
    type: 'status' | 'shift' | 'location'
  ): void {
    if (r.is_grayed) {
      return;
    }

    if (open) {
      // close previously opened dropdown automatically
      this.activeDropdown = {
        rowId: r.id,
        type,
      };
    } else if (
      this.activeDropdown &&
      this.activeDropdown.rowId === r.id &&
      this.activeDropdown.type === type
    ) {
      this.activeDropdown = null;
    }

    this.cdr.markForCheck();
  }

  /** YYYY-MM-DD for API query (matches GET ?date=). */
  formatDate(date: Date): string {
    return this.toIsoLocal(date);
  }

  onCustomRangeChanged(ev: DateRangeChange) {
    this.selectedStartDate = ev.startDate;
    this.selectedEndDate = ev.endDate;
  }

  onDateChange(date: Date | null) {
    if (date) {
      this.reportDate = date;
    }
  }

  onRangeChange(event: DateRangeChange) {
    this.fixStartDate = event.startDate;
    this.fixEndDate = event.endDate;
  }

  onMonthYearChange(event: { month: number; year: number }) {
    this.selectedMonth = event.month;
    this.selectedYear = event.year;
  }

  /** Local calendar YYYY-MM-DD for display labels (Today / Yesterday / etc.). */
  private toIsoLocal(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /** Table rows from API — use in template with `data.length` conditions. */
  get data(): CashReconciliationEntry[] {
    return this.rows;
  }

  get paginationData(): PaginationData {
    const total = this.totalItems;
    const ipp = this.itemsPerPage;
    const totalPages = Math.max(1, Math.ceil(total / ipp));
    const safePage = Math.min(this.currentPage, totalPages);
    return {
      currentPage: safePage,
      totalPages,
      totalItems: total,
      itemsPerPage: ipp,
    };
  }

  onCrPageChange(page: number): void {
    if (page === this.currentPage) {
      return;
    }

    this.currentPage = page;
    this.loadEntries();
  }

  onCrItemsPerPageChange(n: number): void {
    this.itemsPerPage = n;
    this.currentPage = 1;
    this.loadEntries();
  }

  getFormattedDate(): string {
    return this.reconciliationViewDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  formatDisplayDate(): string {
    const selKey = this.toIsoLocal(this.reconciliationViewDate);
    const now = getCashReconciliationToday();
    const todayKey = this.toIsoLocal(now);
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    const yesterdayKey = this.toIsoLocal(y);
    const t = new Date(now);
    t.setDate(t.getDate() + 1);
    const tomorrowKey = this.toIsoLocal(t);

    const short = this.reconciliationViewDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    if (selKey === todayKey) {
      return `Today - ${short}`;
    }
    if (selKey === yesterdayKey) {
      return `Yesterday - ${short}`;
    }
    if (selKey === tomorrowKey) {
      return `Tomorrow - ${short}`;
    }
    return short;
  }

  goToPreviousDay(): void {
    const d = new Date(this.reconciliationViewDate);
    d.setDate(d.getDate() - 1);
    this.reconciliationViewDate = d;
    this.currentPage = 1;
    this.loadEntries();
  }

  goToNextDay(): void {
    const d = new Date(this.reconciliationViewDate);
    d.setDate(d.getDate() + 1);
    if (d.getTime() > getCashReconciliationToday().getTime()) {
      return;
    }
    this.reconciliationViewDate = d;
    this.currentPage = 1;
    this.loadEntries();
  }

  private loadEntries(): void {
    this.loadEntries$.next();
  }

  private loadLocations(): void {
    this.profileService.locations$.pipe(takeUntil(this.destroy$)).subscribe(locations => {
      this.locationSelectOptions = locations.map((loc: any) => ({
        value: loc.id,
        label: loc.branch,
      }));

      this.cdr.markForCheck();
    });

    this.profileService.loadLocations();
  }

  private loadShifts(): void {
    this.employeeService
      .loadShifts()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (shifts: Shift[]) => {
          this.shiftIdToOptionMap = new Map(
            shifts.map(s => [s.id, s.shift_name as ShiftOption]) // ✅ map id → option
          );

          this.shiftDropdownOptions = [
            { label: 'No Shift', value: this.UNASSIGNED },
            ...shifts.map(s => ({
              label: s.shift_name,
              value: s.id,
            })),
          ];
          this.cdr.markForCheck();
        },
        error: () => {
          this.shiftDropdownOptions = [{ label: 'No Shift', value: this.UNASSIGNED }];
          this.cdr.markForCheck();
        },
      });
  }

  private loadEmployeeOptions(): void {
    this.employeeService
      .getEmployeeOptions()
      .pipe(takeUntil(this.destroy$))
      .subscribe(options => {
        this.employeeOptions = options;
        this.cdr.markForCheck();
      });
  }

  draft(r: CashReconciliationEntry): RowDraft {
    return (this.draftById[r.id] ??= {
      opening: r.opening_balance,
      closing: r.closing_balance,
      delta: Number(r.delta ?? 0),
      match_status: normalizeDeltaStatus(r.match_status),
      opening_status: r.opening_status ?? 'send',
    });
  }

  trackById(_i: number, r: CashReconciliationEntry): string {
    return r.id;
  }

  getEmployeeInitials(name: string): string {
    if (!name?.trim()) {
      return '';
    }
    return name
      .split(' ')
      .filter(p => p.length)
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  getStatusTriggerStyle(status: string): { background: string; color: string } {
    const key = normalizeStatus(status);
    const config = STATUS_TRIGGER[key] || DEFAULT_STATUS_TRIGGER;
    return {
      background: config.bg,
      color: config.color,
    };
  }

  isDeltaPending(row: CashReconciliationEntry): boolean {
    return !row.is_grayed && row.closing_balance == null;
  }

  isDeltaMatch(row: CashReconciliationEntry): boolean {
    return !row.is_grayed && row.closing_balance != null && row.delta === 0;
  }

  isDeltaNonzero(row: CashReconciliationEntry): boolean {
    return !row.is_grayed && row.closing_balance != null && row.delta !== 0;
  }

  formatDeltaValue(delta: number): string {
    const a = formatCurrency(Math.abs(delta));
    if (delta === 0) {
      return '$' + a;
    }
    return delta > 0 ? `+$${a}` : `-$${a}`;
  }

  onStatusChange(r: CashReconciliationEntry, v: string): void {
    this.service
      .updateEntry(r.id, { status: v })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: u => {
          this.replaceRow(u);
          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: 'Status updated!',
            detail: `Status has been updated successfully`,
          });
        },
        error: () => {
          this.cdr.markForCheck();
        },
      });
  }

  onShiftChange(r: CashReconciliationEntry, value: string): void {
    const payload: CashReconciliationUpdatePayload = {
      shift_id: value === this.UNASSIGNED ? null : (value as any),
    };

    this.service
      .updateEntry(r.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: u => {
          this.replaceRow(u);
          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: 'Shift updated!',
            detail: `Shift has been updated successfully`,
          });
        },
        error: () => {
          this.cdr.markForCheck();
        },
      });
  }

  onLocationChange(r: CashReconciliationEntry, v: string): void {
    this.service
      .updateEntry(r.id, { location_id: v })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: u => {
          this.replaceRow(u);
          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: 'Location updated!',
            detail: `Location has been updated successfully`,
          });
        },
        error: () => {
          this.cdr.markForCheck();
        },
      });
  }

  onOpeningValueChange(r: CashReconciliationEntry, v: number | null): void {
    const draft = this.draft(r);
    draft.opening = Number(v ?? 0);
    draft.match_status = normalizeDeltaStatus(draft.match_status);
    this.cdr.markForCheck();
  }

  canSendOpening(r: CashReconciliationEntry): boolean {
    if (r.is_grayed) {
      return false;
    }

    if (this.savingOpeningIds.includes(r.id)) {
      return false;
    }

    const draft = this.draft(r);

    // enable when status is send
    if (draft.opening_status === 'send') {
      return true;
    }

    // enable when value changed
    return Number(draft.opening) !== Number(r.opening_balance);
  }

  sendOpening(r: CashReconciliationEntry): void {
    if (this.savingOpeningIds.includes(r.id)) {
      return;
    }

    this.savingOpeningIds = [...this.savingOpeningIds, r.id];

    const p: CashReconciliationUpdatePayload = {
      opening_balance: this.draft(r).opening,
    };

    this.service
      .updateEntry(r.id, p)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: u => {
          this.replaceRow(u);

          // set button state as Sent after successful save
          this.draftById[r.id].opening_status = 'sent';

          this.savingOpeningIds = this.savingOpeningIds.filter(id => id !== r.id);

          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: 'Opening balance updated!',
            detail: `Opening balance has been updated successfully`,
          });
          this.cdr.markForCheck();
        },

        error: () => {
          this.savingOpeningIds = this.savingOpeningIds.filter(id => id !== r.id);

          this.cdr.markForCheck();
        },
      });
  }

  onClosingValueChange(r: CashReconciliationEntry, v: number | null): void {
    const draft = this.draft(r);
    draft.closing = v;
    const openingStatus =
      Number(draft.opening) !== Number(r.opening_balance) ? 'send' : draft.opening_status;

    draft.match_status = normalizeDeltaStatus(draft.match_status);
    this.cdr.markForCheck();
  }

  canSubmitClosing(r: CashReconciliationEntry): boolean {
    if (r.is_grayed) {
      return false;
    }

    if (this.savingClosingIds.includes(r.id)) {
      return false;
    }

    const c = this.draft(r).closing;

    if (c === null || c === undefined) {
      return false;
    }

    return Number(c) !== Number(r.closing_balance);
  }

  submitClosing(r: CashReconciliationEntry): void {
    if (this.savingClosingIds.includes(r.id)) {
      return;
    }

    this.savingClosingIds = [...this.savingClosingIds, r.id];

    const c = this.draft(r).closing;

    this.service
      .updateEntry(r.id, { closing_balance: c })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: u => {
          this.replaceRow(u);

          this.savingClosingIds = this.savingClosingIds.filter(id => id !== r.id);

          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: 'Closing balance updated!',
            detail: `Closing balance has been updated successfully`,
          });
          //this.cdr.markForCheck();
        },

        error: () => {
          this.savingClosingIds = this.savingClosingIds.filter(id => id !== r.id);

          this.cdr.markForCheck();
        },
      });
  }

  getShiftValue(row: CashReconciliationEntry): string {
    return row.shift_id ?? this.UNASSIGNED;
  }

  private replaceRow(res: any): void {
    const updated = res?.data ?? res;

    const index = this.rows.findIndex(r => r.id === updated.id);
    if (index < 0) {
      return;
    }

    const current = this.rows[index];

    const newRow: CashReconciliationEntry = {
      ...current,
      ...updated,

      opening_status:
        updated.opening_status ??
        this.draftById[updated.id]?.opening_status ??
        current.opening_status,

      status: normalizeStatus(updated.status),
      match_status: normalizeDeltaStatus(updated.match_status),
      opening_balance: Number(updated.opening_balance ?? 0),
      closing_balance: updated.closing_balance == null ? 0 : Number(updated.closing_balance),

      delta: Number(updated.delta ?? 0),
    };

    this.rows = this.rows.map(r => (r.id === newRow.id ? newRow : r));

    const existingDraft = this.draftById[newRow.id];

    this.draftById[newRow.id] = {
      opening: newRow.opening_balance,
      closing: newRow.closing_balance,
      delta: newRow.delta,
      match_status: newRow.match_status,

      opening_status:
        newRow.opening_status ?? existingDraft?.opening_status ?? current.opening_status ?? 'send',
    };

    this.cdr.markForCheck();
  }

  extraHistoryList(id: string): { time: string; amount: number }[] {
    const row = this.rows.find(r => r.id === id);

    if (!row) {
      return [];
    }

    return row.extra_cash.map(c => ({
      amount: c.amount,
      time: this.formatExtraCashTime(c.time),
    }));
  }

  formatExtraCashTime(timestamp: string): string {
    if (!timestamp) {
      return '--';
    }

    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  toggleAddCash(r: CashReconciliationEntry, ev: Event, trigger: HTMLElement): void {
    ev.stopPropagation();

    if (r.is_grayed) {
      return;
    }

    if (this.addCashFor?.id === r.id) {
      this.closeAddCash();
      return;
    }

    this.addCashFor = r;
    this.addCashDraft = 0;

    requestAnimationFrame(() => {
      const popup = document.querySelector('.cr-add-pop') as HTMLElement | null;

      if (!popup) {
        return;
      }

      this.updateAddCashPopupPosition(trigger, popup);
    });
  }

  closeAddCash(): void {
    this.removeAddCashScroll();

    this.addCashFor = null;
    this.addCashDraft = 0;
  }

  confirmAddCash(): void {
    if (!this.addCashFor || this.addCashDraft <= 0) {
      this.closeAddCash();
      return;
    }

    const id = this.addCashFor.id;

    if (this.savingExtraCashIds.includes(id)) {
      return;
    }

    this.savingExtraCashIds = [...this.savingExtraCashIds, id];

    const amt = this.addCashDraft;

    this.service
      .addExtraCash(id, { amount: amt })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: updated => {
          this.updateExtraCashRow(updated);

          this.savingExtraCashIds = this.savingExtraCashIds.filter(x => x !== id);

          //this.closeAddCash();
          // clear input after success
          this.addCashDraft = 0;

          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: 'Extra cash added!',
            detail: `Extra cash has been added successfully`,
          });
          this.cdr.markForCheck();
        },

        error: () => {
          this.savingExtraCashIds = this.savingExtraCashIds.filter(x => x !== id);

          this.cdr.markForCheck();
        },
      });
  }

  private updateExtraCashRow(updated: any): void {
    const index = this.rows.findIndex(r => r.id === updated.id);

    if (index === -1) {
      return;
    }

    const current = this.rows[index];
    const newRow: CashReconciliationEntry = {
      ...current,
      extra_cash: (updated.extra_cash || []).map((cash: any) => ({
        amount: Number(cash.amount ?? 0),
        time: cash.timestamp,
      })),
      extra_cash_total: Number(updated.extra_cash_total ?? 0),
      expected_closing: Number(updated.expected_closing ?? 0),
      delta: Number(updated.delta ?? 0),
      match_status: normalizeDeltaStatus(updated.match_status),
    };

    this.rows = this.rows.map(r => (r.id === updated.id ? newRow : r));

    this.cdr.markForCheck();
  }

  onAddCashValueChange(value: number | null): void {
    this.addCashDraft = value ?? 0;
  }

  canAddCash(): boolean {
    return (
      this.addCashDraft > 0 &&
      !!this.addCashFor &&
      !this.savingExtraCashIds.includes(this.addCashFor.id)
    );
  }

  private nowTimeLabel(): string {
    return new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  onViewPaymentDetails(r: CashReconciliationEntry): void {
    if (r.is_grayed) return;
    this.paymentModal = r;
    this.paymentTx = [];
    this.paymentTxLoading = true;
    this.paymentModalOpen = true;

    const date = this.formatDate(this.reconciliationViewDate);

    this.service
      .getTransactions(r.employee?.id, date)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.paymentTx = mapTransactionsResponse(res);
          this.paymentTxLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.paymentTxLoading = false;
        },
      });
  }

  closePaymentModal(): void {
    this.paymentModalOpen = false;
    this.paymentModal = null;
    this.paymentTx = [];
  }

  paymentTotal(): number {
    return this.paymentTx.reduce((a, t) => a + t.amount, 0);
  }

  onViewReport(_r: CashReconciliationEntry): void {
    this.reportLoading = true;
    this.selectedReportType = 'daily';
    this.reportDate = new Date(this.reconciliationViewDate);

    // store selected employee id
    this.selectedReportEmployeeId = _r.employee?.id ?? null;

    const dateUtc = this.toUtcYmd(this.reportDate);

    this.service
      .getReport({
        type: 'daily',
        dateUtc,
        employee_ids: [_r.employee?.id ?? ''],
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.applyReportResult(res, 'daily');
          this.cdr.markForCheck();
        },

        error: () => {
          this.reportLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  onFixPastData(): void {
    this.fixPastModalOpen = true;
    this.fixSearchLoading = false;
    this.savingEmployeeIds = [];
    // CLEAR FORM VALUES
    this.fixStartDate = null;
    this.fixEndDate = null;
    this.selectedEmployeeIds = [];

    this.resetFixModalSession();
    this.cdr.markForCheck();
  }

  onGenerateReport(): void {
    this.initReportForm();
    this.reportTypeModalOpen = true;
  }

  get isReportCustomValid(): boolean {
    if (!this.selectedStartDate || !this.selectedEndDate) {
      return false;
    }
    const a = this.stripReport(this.selectedStartDate);
    const b = this.stripReport(this.selectedEndDate);
    const diff = (b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24) + 1;
    return diff >= 1 && diff <= 30;
  }

  /** True when both ends chosen but span exceeds 30 days. */
  get showReportCustomRangeError(): boolean {
    if (!this.selectedStartDate || !this.selectedEndDate) {
      return false;
    }
    return !this.isReportCustomValid;
  }

  get customRangeDisplayLine(): string {
    if (!this.selectedStartDate) {
      return '';
    }
    const a = this.formatReportModalDate(this.selectedStartDate);
    if (!this.selectedEndDate) {
      return `${a} -`;
    }
    return `${a} - ${this.formatReportModalDate(this.selectedEndDate)}`;
  }

  get canSubmitReportGenerate(): boolean {
    if (this.selectedReportType === 'daily') {
      return !!this.reportDate;
    }

    if (this.selectedReportType === 'monthly') {
      return !!this.selectedMonth && !!this.selectedYear;
    }

    if (this.selectedReportType === 'custom') {
      if (!this.selectedStartDate || !this.selectedEndDate) {
        return false;
      }

      const diff =
        Math.floor((this.selectedEndDate.getTime() - this.selectedStartDate.getTime()) / 86400000) +
        1;

      return diff >= 1 && diff <= 30;
    }

    return false;
  }

  closeReportTypeModal(): void {
    this.reportTypeModalOpen = false;
  }

  closeReportResultModal(): void {
    this.reportResultModalOpen = false;
  }

  closeReportDateRangeModal(): void {
    this.reportDateRangeModalOpen = false;
  }

  cancelReportTypeModal(): void {
    this.closeReportTypeModal();
  }

  onReportGenerateClick(): void {
    if (!this.canSubmitReportGenerate) {
      return;
    }

    this.reportLoading = true;
    if (this.selectedReportType === 'daily') {
      const dateUtc = this.toUtcYmd(this.reportDate);
      this.service
        .getReport({ type: 'daily', dateUtc })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: res => {
            this.applyReportResult(res, 'daily');
            this.cdr.markForCheck();
          },
          error: () => {
            this.reportLoading = false;
          },
        });
    } else if (this.selectedReportType === 'monthly') {
      const year = String(this.selectedYear);
      const month = String(this.selectedMonth).padStart(2, '0');
      this.service
        .getReport({ type: 'monthly', month, year })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: res => {
            this.applyReportResult(res, 'monthly');
            this.cdr.markForCheck();
          },
          error: () => {
            this.reportLoading = false;
          },
        });
    } else {
      const startUtc = this.toUtcYmd(this.selectedStartDate!);
      const endUtc = this.toUtcYmd(this.selectedEndDate!);
      this.service
        .getReport({ type: 'custom', startUtc, endUtc })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: res => {
            this.applyReportResult(res, 'custom');
            this.cdr.markForCheck();
          },
          error: () => {
            this.reportLoading = false;
          },
        });
    }
  }

  private applyReportResult(
    res: CashReconciliationReportResponse,
    type: 'daily' | 'monthly' | 'custom'
  ): void {
    this.reportData = res.rows;
    this.reportGridData = mapReportToGrid(res.rows);
    this.reportDateRangeLabel = res.date_range_label;
    this.reportLocationLabel = res.location;
    this.reportTotalEntries = res.total_entries;
    this.reportTotalMatched = res.total_matched;
    this.reportTotalNotMatched = res.total_not_matched;
    this.reportSumDeltas = res.sum_of_deltas;
    this.reportGeneratedAt = new Date();
    this.reportLoading = false;
    this.reportTypeModalOpen = false;

    if (type === 'daily') {
      this.reportResultModalOpen = true;
    } else {
      this.reportDateRangeModalOpen = true;
    }
  }

  onReportRangeTypeChange(): void {
    // reusable component handles calendar switching
  }

  formatReportModalDate(d: Date): string {
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  trackByReportRowId(_: number, r: CashReconciliationReportRow): string {
    return r.id;
  }

  twoDigit(n: number): string {
    if (n < 0) {
      return String(n);
    }
    return n.toString().padStart(2, '0');
  }

  formatReportCurrency(n: number | null): string {
    if (n == null) {
      return '—';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(n);
  }

  reportDeltaClass(
    delta: number | null
  ): 'cr-rep-delta--pos' | 'cr-rep-delta--neg' | 'cr-rep-delta--muted' {
    if (delta == null) {
      return 'cr-rep-delta--muted';
    }
    return delta < 0 ? 'cr-rep-delta--neg' : 'cr-rep-delta--pos';
  }

  formatReportGeneratedAt(d: Date | null): string {
    if (!d) {
      return '—';
    }
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  downloadReportAsPdf(): void {
    let startDate = '';
    let endDate = '';

    if (this.selectedReportType === 'daily') {
      startDate = this.toUtcYmd(this.reportDate);
      endDate = startDate;
    } else if (this.selectedReportType === 'monthly') {
      const year = Number(this.selectedYear);
      const month = Number(this.selectedMonth);

      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);

      startDate = this.toUtcYmd(firstDay);
      endDate = this.toUtcYmd(lastDay);
    } else {
      if (!this.selectedStartDate || !this.selectedEndDate) {
        return;
      }

      startDate = this.toUtcYmd(this.selectedStartDate);
      endDate = this.toUtcYmd(this.selectedEndDate);
    }

    this.service
      .downloadReportPdf({
        startDate,
        endDate,
        employee_ids: this.selectedReportEmployeeId ? [this.selectedReportEmployeeId] : [],
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const blob = response.body;

          if (!blob) {
            return;
          }
          this.selectedReportEmployeeId = null;
          let fileName = 'cash-reconciliation-report.pdf';

          const contentDisposition = response.headers.get('content-disposition');

          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);

            if (match?.[1]) {
              fileName = match[1];
            }
          }

          const url = window.URL.createObjectURL(blob);

          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;

          document.body.appendChild(a);
          a.click();

          document.body.removeChild(a);

          window.URL.revokeObjectURL(url);
        },
        error: err => {
          console.error('Failed to download report PDF', err);
        },
      });
  }

  private initReportForm(): void {
    this.selectedReportType = 'daily';

    const d = getCashReconciliationToday();

    this.selectedYear = d.getFullYear();
    this.selectedMonth = d.getMonth() + 1;

    this.selectedStartDate = null;
    this.selectedEndDate = null;
  }

  private stripReport(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  closeFixPastModal(): void {
    this.fixPastModalOpen = false;
    this.fixSearchLoading = false;
    this.savingEmployeeIds = [];

    // CLEAR FORM VALUES
    this.fixStartDate = null;
    this.fixEndDate = null;
    this.selectedEmployeeIds = [];
    this.resetFixModalSession();
    this.cdr.markForCheck();
  }

  get selectedEmployees(): MultiSelectOption[] {
    const set = new Set(this.selectedEmployeeIds);
    return this.employeeOptions.filter(e => set.has(e.id));
  }

  get isDateRangeValid(): boolean {
    if (!this.fixStartDate || !this.fixEndDate) {
      return false;
    }
    const diff =
      (this.fixEndDate.getTime() - this.fixStartDate.getTime()) / (1000 * 60 * 60 * 24) + 1;
    return diff <= 30;
  }

  onSelectedEmployeeIdsChange(ids: string[]): void {
    this.selectedEmployeeIds = [...new Set(ids)];
  }

  /** Kept in sync with Fix Past Submit: `!isDateRangeValid || selectedEmployeeIds.length === 0 || fixSearchLoading` */
  isSubmitDisabled(): boolean {
    return this.fixSearchLoading || this.selectedEmployeeIds.length === 0 || !this.isDateRangeValid;
  }

  submitFixPastData(): void {
    if (this.isSubmitDisabled()) {
      return;
    }

    this.fixShowResultsView = true;
    this.fixSearchLoading = true;

    const startUtc = this.toUtcYmd(this.fixStartDate!);
    const endUtc = this.toUtcYmd(this.fixEndDate!);

    this.service
      .getFixData(startUtc, endUtc, this.selectedEmployeeIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          const mapped = mapFixDataResponse(result);
          this.fullData = mapped;
          this.displayedData = JSON.parse(JSON.stringify(mapped));
          this.fixResultsSnapshot = JSON.parse(JSON.stringify(mapped));

          this.fixHasSearched = true;
          this.fixSearchLoading = false;
          this.rebuildChangedEntriesMap();

          this.cdr.markForCheck();
        },
        error: () => {
          this.fixSearchLoading = false;
          this.fixHasSearched = true;
        },
      });
  }

  onSaveFixEmployeeRow(payload: ReconciliationGridSavePayload): void {
    if (payload.data.length === 0 || this.savingEmployeeIds.includes(payload.employeeId)) {
      return;
    }

    this.savingEmployeeIds = [...this.savingEmployeeIds, payload.employeeId];

    const bulkPayload = payload.data.map(x => ({
      reconciliation_id: x.id,
      ...(x.opening_balance !== undefined && {
        opening_balance: x.opening_balance,
      }),
      ...(x.closing_balance !== undefined && {
        closing_balance: x.closing_balance,
      }),
    }));

    this.service
      .bulkUpdate(bulkPayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // UPDATE ONLY CHANGED ROWS
          this.displayedData = this.displayedData.map(emp => {
            if (emp.employee_id !== payload.employeeId) {
              return emp;
            }

            return {
              ...emp,
              entries: emp.entries.map(entry => {
                const changed = payload.data.find(x => x.id === entry.id);

                if (!changed) {
                  return entry;
                }

                return {
                  ...entry,
                  opening_balance: changed.opening_balance ?? entry.opening_balance,

                  closing_balance: changed.closing_balance ?? entry.closing_balance,
                };
              }),
            };
          });

          // ALSO UPDATE SNAPSHOT
          this.fixResultsSnapshot = this.fixResultsSnapshot.map(emp => {
            if (emp.employee_id !== payload.employeeId) {
              return emp;
            }

            return {
              ...emp,
              entries: emp.entries.map(entry => {
                const changed = payload.data.find(x => x.id === entry.id);

                if (!changed) {
                  return entry;
                }

                return {
                  ...entry,
                  opening_balance: changed.opening_balance ?? entry.opening_balance,

                  closing_balance: changed.closing_balance ?? entry.closing_balance,
                };
              }),
            };
          });

          this.rebuildChangedEntriesMap();

          this.savingEmployeeIds = this.savingEmployeeIds.filter(id => id !== payload.employeeId);

          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: 'Fix past data saved!',
            detail: `Fix past data has been saved successfully`,
          });

          this.cdr.markForCheck();
        },

        error: () => {
          this.savingEmployeeIds = this.savingEmployeeIds.filter(id => id !== payload.employeeId);

          this.cdr.markForCheck();
        },
      });
  }

  onCancelFixEmployee(employeeId: string): void {
    const snap = this.fixResultsSnapshot.find(r => r.employee_id === String(employeeId));
    if (!snap) {
      return;
    }
    const fresh = this.cloneFixRows([snap])[0];
    this.displayedData = this.displayedData.map(row =>
      row.employee_id === String(employeeId) ? fresh : row
    );
    this.rebuildChangedEntriesMap();
  }

  rebuildChangedEntriesMap(): void {
    const m: Record<string, Record<string, any>> = {};

    const snapMap = new Map(this.fixResultsSnapshot.map(s => [s.employee_id, s]));

    for (const emp of this.displayedData) {
      const snap = snapMap.get(emp.employee_id);
      if (!snap) continue;

      for (const entry of emp.entries) {
        const orig = snap.entries.find(e => e.id === entry.id);
        if (!orig || !entry.is_editable) continue;

        if (
          entry.opening_balance === orig.opening_balance &&
          entry.closing_balance === orig.closing_balance
        )
          continue;

        if (!m[emp.employee_id]) {
          m[emp.employee_id] = {};
        }

        m[emp.employee_id][entry.id] = {
          opening_balance: entry.opening_balance,
          closing_balance: entry.closing_balance,
        };
      }
    }

    this.changedEntriesMap = m;
  }

  private resetFixModalSession(): void {
    this.fixShowResultsView = false;
    this.fixHasSearched = false;
    this.fullData = [];
    this.displayedData = [];
    this.fixResultsSnapshot = [];
    this.changedEntriesMap = {};
  }

  private toUtcYmd(d: Date): string {
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
      .toISOString()
      .slice(0, 10);
  }

  private cloneFixRows(rows: FixReconciliationEmployee[]): ReconciliationGridEmployee[] {
    return rows.map(r => ({
      employee_id: r.employee_id,
      employee_name: r.employee_name,
      entries: r.entries.map(e => ({ ...e, match_status: e.match_status })),
    }));
  }

  toggleCalendarPopover(event: Event): void {
    event.stopPropagation();
    this.calendarPopoverOpen = !this.calendarPopoverOpen;
  }

  private addCashScrollHandler = () => {
    if (!this.addCashFor) {
      return;
    }

    const trigger = document.querySelector('.add-cash-btn--active') as HTMLElement | null;

    const popup = document.querySelector('.cr-add-pop') as HTMLElement | null;

    if (!trigger || !popup) {
      return;
    }

    this.updateAddCashPopupPosition(trigger, popup);
  };

  private bindAddCashScroll(): void {
    window.addEventListener('scroll', this.addCashScrollHandler, true);
  }

  private removeAddCashScroll(): void {
    window.removeEventListener('scroll', this.addCashScrollHandler, true);
  }

  private updateAddCashPopupPosition(trigger: HTMLElement, popup: HTMLElement): void {
    const rect = trigger.getBoundingClientRect();

    const popupHeight = popup.offsetHeight || 260;
    const viewportHeight = window.innerHeight;

    const spaceBelow = viewportHeight - rect.bottom;

    const openUp = spaceBelow < popupHeight && rect.top > popupHeight;

    const top = openUp ? rect.top - popupHeight : rect.bottom;

    popup.style.left = `${rect.left}px`;
    popup.style.top = `${top}px`;
  }

  onReconciliationDateSelect(date: Date | null): void {
    if (!date) return;
    this.reconciliationViewDate = new Date(date);
    this.calendarPopoverOpen = false;
    this.currentPage = 1;
    // immediately reload table using selected date
    this.loadEntries();
  }

  private reinitDragScroll(): void {
    setTimeout(() => {
      const wrapper = this.cashTableWrapper?.nativeElement;

      if (!wrapper) {
        return;
      }

      // cleanup old listeners
      TableUtils.cleanup(wrapper);

      // recreate config
      this.cashDragScrollConfig = TableUtils.createDragScrollConfig(this.cashTableWrapper);

      // rebind listeners
      TableUtils.initializeDragScrolling(this.cashDragScrollConfig);
    }, 0);
  }
}
